import React, { useCallback, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import {
  LuBox,
  LuCalendar,
  LuChevronLeft,
  LuChevronRight,
  LuCircleCheck,
  LuClipboardList,
  LuClock3,
  LuCreditCard,
  LuEllipsis,
  LuEye,
  LuFileText,
  LuHourglass,
  LuLightbulb,
  LuPaperclip,
  LuSearch,
  LuUserCog,
  LuWrench,
  LuX,
} from "react-icons/lu";
import { resolvePublicFileUrl } from "../../constants/api-urls";
import {
  TICKET_CATEGORY_OPTIONS,
  TICKET_PRIORITY_OPTIONS,
  TICKET_STATUS_OPTIONS,
  fetchMySupportTicketById,
  fetchMySupportTicketStats,
  fetchMySupportTickets,
  ticketExtractMessage,
} from "../../services/supportTicket.service";
import "../../assets/styles/my_disputes.scss";

const PAGE_OPTIONS = [10, 20, 50];

function formatDate(value) {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleDateString("en-US", {
    month: "2-digit",
    day: "2-digit",
    year: "numeric",
  });
}

function formatTime(value) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleTimeString("en-US", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
}

function formatDateTime(value) {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleString("en-US", {
    month: "2-digit",
    day: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
}

function formatStatus(status, label) {
  if (label) return label;
  return String(status || "")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

function truncateText(value, max = 72) {
  const text = String(value || "").trim();
  if (!text) return "—";
  if (text.length <= max) return text;
  return `${text.slice(0, max - 1)}…`;
}

function statusClass(status) {
  const normalized = String(status || "").toLowerCase();
  return `md-pill md-status md-status--${normalized.replace(/\s+/g, "_")}`;
}

function priorityClass(priority) {
  const normalized = String(priority || "").toLowerCase();
  if (normalized === "urgent" || normalized === "critical") {
    return "md-pill md-priority md-priority--urgent";
  }
  if (normalized === "high") return "md-pill md-priority md-priority--high";
  if (normalized === "medium") return "md-pill md-priority md-priority--medium";
  return "md-pill md-priority md-priority--low";
}

function categoryIcon(categoryCode) {
  const map = {
    account_profile: LuUserCog,
    technical: LuWrench,
    payment_billing: LuCreditCard,
    product_listing: LuBox,
    orders_transactions: LuClipboardList,
    feature_request: LuLightbulb,
    other: LuEllipsis,
  };
  return map[String(categoryCode || "").toLowerCase()] || LuFileText;
}

function buildPageNumbers(currentPage, totalPages, maxVisible = 5) {
  if (totalPages <= maxVisible) {
    return Array.from({ length: totalPages }, (_, index) => index + 1);
  }
  let start = Math.max(1, currentPage - Math.floor(maxVisible / 2));
  let end = Math.min(totalPages, start + maxVisible - 1);
  start = Math.max(1, end - maxVisible + 1);
  return Array.from({ length: end - start + 1 }, (_, index) => start + index);
}

export default function MyTicketsPage() {
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const [status, setStatus] = useState("");
  const [category, setCategory] = useState("");
  const [priority, setPriority] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    total: 0,
    inProgress: 0,
    resolved: 0,
    closed: 0,
  });
  const [quickView, setQuickView] = useState(null);
  const [quickViewLoading, setQuickViewLoading] = useState(false);
  const [quickViewError, setQuickViewError] = useState("");

  const closeQuickView = () => {
    setQuickView(null);
    setQuickViewLoading(false);
    setQuickViewError("");
  };

  const openQuickView = async (ticket) => {
    const ticketId = Number(ticket?.id);
    if (!Number.isFinite(ticketId) || ticketId <= 0) return;
    setQuickView({ ticket });
    setQuickViewError("");
    setQuickViewLoading(true);
    try {
      const detail = await fetchMySupportTicketById(ticketId);
      if (detail?.ticket) {
        setQuickView(detail);
      } else {
        setQuickViewError("Ticket details are unavailable.");
      }
    } catch (error) {
      setQuickViewError(ticketExtractMessage(error, "Failed to load ticket details."));
    } finally {
      setQuickViewLoading(false);
    }
  };

  const loadStats = useCallback(async () => {
    try {
      const data = await fetchMySupportTicketStats();
      setStats({
        total: Math.trunc(Number(data.total) || 0),
        inProgress: Math.trunc(Number(data.inProgress) || 0),
        resolved: Math.trunc(Number(data.resolved) || 0),
        closed: Math.trunc(Number(data.closed) || 0),
      });
    } catch {
      // Keep prior stats.
    }
  }, []);

  const loadTickets = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchMySupportTickets({
        search: query.trim() || undefined,
        status: status || undefined,
        categoryCode: category || undefined,
        priority: priority || undefined,
        dateFrom: dateFrom || undefined,
        dateTo: dateTo || undefined,
        page,
        pageSize,
      });
      setItems(Array.isArray(data.items) ? data.items : []);
      setTotal(Math.trunc(Number(data.total) || 0));
    } catch (error) {
      toast.error(ticketExtractMessage(error, "Failed to load your tickets."));
      setItems([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [query, status, category, priority, dateFrom, dateTo, page, pageSize]);

  useEffect(() => {
    loadStats();
  }, [loadStats]);

  useEffect(() => {
    const timer = setTimeout(loadTickets, 250);
    return () => clearTimeout(timer);
  }, [loadTickets]);

  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const currentPage = Math.min(page, totalPages);
  const pageNumbers = useMemo(
    () => buildPageNumbers(currentPage, totalPages),
    [currentPage, totalPages],
  );

  const resetFilters = () => {
    setQuery("");
    setStatus("");
    setCategory("");
    setPriority("");
    setDateFrom("");
    setDateTo("");
    setPage(1);
  };

  const openDetail = (ticket) => {
    navigate(`/dashboard/help-center/my-tickets/${ticket.id}`);
  };

  return (
    <div className="md-page">
      <header className="md-head">
        <div>
          <nav className="md-breadcrumb" aria-label="Breadcrumb">
            <Link to="/home">Home</Link>
            <span aria-hidden>&gt;</span>
            <Link to="/help-center">Help Center</Link>
            <span aria-hidden>&gt;</span>
            <span aria-current="page">My Tickets</span>
          </nav>
          <h1>My Tickets</h1>
          <p>Track and manage all your submitted support tickets in one place.</p>
        </div>
        <div className="md-head__actions">
          <button
            type="button"
            className="md-btn md-btn--primary"
            onClick={() => navigate("/help-center/raise-ticket")}
          >
            Raise Ticket
          </button>
        </div>
      </header>

      <section className="md-stats">
        <article className="md-stat">
          <span className="md-stat__icon md-stat__icon--blue">
            <LuClipboardList aria-hidden />
          </span>
          <div>
            <strong>{stats.total.toLocaleString()}</strong>
            <span>Total Tickets</span>
            <small>All time</small>
          </div>
        </article>
        <article className="md-stat">
          <span className="md-stat__icon md-stat__icon--orange">
            <LuHourglass aria-hidden />
          </span>
          <div>
            <strong>{stats.inProgress.toLocaleString()}</strong>
            <span>In Progress</span>
            <small>Active tickets</small>
          </div>
        </article>
        <article className="md-stat">
          <span className="md-stat__icon md-stat__icon--green">
            <LuCircleCheck aria-hidden />
          </span>
          <div>
            <strong>{stats.resolved.toLocaleString()}</strong>
            <span>Resolved</span>
            <small>Successfully closed</small>
          </div>
        </article>
        <article className="md-stat">
          <span className="md-stat__icon md-stat__icon--purple">
            <LuClock3 aria-hidden />
          </span>
          <div>
            <strong>{stats.closed.toLocaleString()}</strong>
            <span>Closed</span>
            <small>No further action</small>
          </div>
        </article>
      </section>

      <div className="md-toolbar">
        <label className="md-search">
          <LuSearch aria-hidden />
          <input
            value={query}
            onChange={(event) => {
              setQuery(event.target.value);
              setPage(1);
            }}
            placeholder="Search by Ticket ID, Subject, Reference..."
          />
        </label>
        <select
          value={status}
          onChange={(event) => {
            setStatus(event.target.value);
            setPage(1);
          }}
        >
          {TICKET_STATUS_OPTIONS.map((opt) => (
            <option key={opt.value || "all-status"} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
        <select
          value={category}
          onChange={(event) => {
            setCategory(event.target.value);
            setPage(1);
          }}
        >
          {TICKET_CATEGORY_OPTIONS.map((opt) => (
            <option key={opt.value || "all-category"} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
        <select
          value={priority}
          onChange={(event) => {
            setPriority(event.target.value);
            setPage(1);
          }}
        >
          {TICKET_PRIORITY_OPTIONS.map((opt) => (
            <option key={opt.value || "all-priority"} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
        <label className="md-date">
          <LuCalendar aria-hidden />
          <input
            type="date"
            value={dateFrom}
            onChange={(event) => {
              setDateFrom(event.target.value);
              setPage(1);
            }}
            aria-label="From date"
          />
        </label>
        <label className="md-date">
          <LuCalendar aria-hidden />
          <input
            type="date"
            value={dateTo}
            onChange={(event) => {
              setDateTo(event.target.value);
              setPage(1);
            }}
            aria-label="To date"
          />
        </label>
        <button type="button" className="md-btn md-btn--ghost" onClick={resetFilters}>
          Reset
        </button>
      </div>

      <section className="md-card">
        <div className="md-table-wrap">
          <table className="md-table">
            <thead>
              <tr>
                <th>Ticket ID</th>
                <th>Reference</th>
                <th>Category</th>
                <th>Subject</th>
                <th>Status</th>
                <th>Priority</th>
                <th>Submitted On</th>
                <th>Last Updated</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={9} className="md-empty">
                    Loading tickets...
                  </td>
                </tr>
              ) : items.length === 0 ? (
                <tr>
                  <td colSpan={9} className="md-empty">
                    No tickets found.{" "}
                    <button
                      type="button"
                      className="md-link-btn"
                      onClick={() => navigate("/help-center/raise-ticket")}
                    >
                      Raise a ticket
                    </button>
                  </td>
                </tr>
              ) : (
                items.map((ticket) => {
                  const CategoryIcon = categoryIcon(ticket.categoryCode);
                  return (
                    <tr key={ticket.id}>
                      <td>
                        <div className="md-id-cell">
                          <button
                            type="button"
                            className="md-id-link"
                            onClick={() => openQuickView(ticket)}
                          >
                            {ticket.ticketNumber}
                          </button>
                          <span className="md-id-chip">
                            #{String(ticket.id).padStart(6, "0")}
                          </span>
                        </div>
                      </td>
                      <td>
                        <div className="md-stack">
                          {ticket.referenceNumber ? (
                            <>
                              <strong title={ticket.referenceNumber}>
                                {truncateText(ticket.referenceNumber, 22)}
                              </strong>
                              {ticket.affectedModule ? (
                                <span title={ticket.affectedModule}>
                                  {truncateText(ticket.affectedModule, 24)}
                                </span>
                              ) : null}
                            </>
                          ) : ticket.affectedModule ? (
                            <strong title={ticket.affectedModule}>
                              {truncateText(ticket.affectedModule, 28)}
                            </strong>
                          ) : (
                            <strong>—</strong>
                          )}
                        </div>
                      </td>
                      <td>
                        <div className="md-category">
                          <span className="md-category__icon" aria-hidden>
                            <CategoryIcon />
                          </span>
                          <span title={ticket.categoryLabel}>
                            {truncateText(ticket.categoryLabel, 28)}
                          </span>
                        </div>
                      </td>
                      <td>
                        <div className="md-stack">
                          <strong title={ticket.subject}>
                            {truncateText(ticket.subject, 42)}
                          </strong>
                          <span title={ticket.description}>
                            {truncateText(ticket.description, 54)}
                          </span>
                        </div>
                      </td>
                      <td>
                        <span className={statusClass(ticket.status)}>
                          {formatStatus(ticket.status, ticket.statusLabel)}
                        </span>
                      </td>
                      <td>
                        <span className={priorityClass(ticket.priority)}>
                          {ticket.priority}
                        </span>
                      </td>
                      <td>
                        <div className="md-stack">
                          <strong>{formatDate(ticket.createdAt)}</strong>
                          <span>{formatTime(ticket.createdAt)}</span>
                        </div>
                      </td>
                      <td>
                        <div className="md-stack">
                          <strong>{formatDate(ticket.updatedAt)}</strong>
                          <span>{formatTime(ticket.updatedAt)}</span>
                        </div>
                      </td>
                      <td>
                        <button
                          type="button"
                          className="md-icon-btn"
                          title="View details"
                          onClick={() => openDetail(ticket)}
                        >
                          <LuEye aria-hidden />
                        </button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        <footer className="md-footer">
          <span>
            Showing {items.length ? (currentPage - 1) * pageSize + 1 : 0} to{" "}
            {Math.min(currentPage * pageSize, total)} of {total} tickets
          </span>
          <div className="md-pagination">
            <button
              type="button"
              disabled={currentPage <= 1}
              onClick={() => setPage((value) => Math.max(1, value - 1))}
            >
              <LuChevronLeft aria-hidden />
            </button>
            {pageNumbers.map((item) => (
              <button
                key={item}
                type="button"
                className={currentPage === item ? "is-active" : ""}
                onClick={() => setPage(item)}
              >
                {item}
              </button>
            ))}
            <button
              type="button"
              disabled={currentPage >= totalPages}
              onClick={() => setPage((value) => Math.min(totalPages, value + 1))}
            >
              <LuChevronRight aria-hidden />
            </button>
            <select
              value={pageSize}
              onChange={(event) => {
                setPageSize(Number(event.target.value));
                setPage(1);
              }}
            >
              {PAGE_OPTIONS.map((size) => (
                <option key={size} value={size}>
                  {size} per page
                </option>
              ))}
            </select>
          </div>
        </footer>
      </section>

      <aside className="md-help-banner">
        <div>
          <strong>How support tickets work?</strong>
          <p>
            Learn about ticket priorities, response timelines, and how our support team resolves
            your requests.
          </p>
        </div>
        <Link to="/help-center" className="md-btn md-btn--ghost">
          Learn More
        </Link>
      </aside>

      {quickView?.ticket
        ? createPortal(
            <div
              className="md-modal"
              role="dialog"
              aria-modal="true"
              aria-labelledby="md-ticket-quick-view-title"
              onClick={closeQuickView}
            >
              <div
                className="md-modal__panel"
                onClick={(event) => event.stopPropagation()}
              >
                <header className="md-modal__head">
                  <div className="md-modal__head-main">
                    <div className="md-modal__title-row">
                      <h2 id="md-ticket-quick-view-title">
                        {quickView.ticket.ticketNumber}
                      </h2>
                      <span className="md-id-chip">
                        #{String(quickView.ticket.id).padStart(6, "0")}
                      </span>
                    </div>
                    <p className="md-modal__subject">
                      {quickView.ticket.subject || "Ticket quick view"}
                    </p>
                    <div className="md-modal__badges">
                      <span className={priorityClass(quickView.ticket.priority)}>
                        {quickView.ticket.priority || "—"}
                      </span>
                      <span className={statusClass(quickView.ticket.status)}>
                        {formatStatus(
                          quickView.ticket.status,
                          quickView.ticket.statusLabel,
                        )}
                      </span>
                      {quickView.ticket.slaRemainingLabel ? (
                        <span
                          className={`md-pill${
                            quickView.ticket.slaOverdue ? " md-priority--urgent" : ""
                          }`}
                        >
                          SLA: {quickView.ticket.slaRemainingLabel}
                        </span>
                      ) : null}
                    </div>
                  </div>
                  <button
                    type="button"
                    className="md-icon-btn"
                    aria-label="Close"
                    onClick={closeQuickView}
                  >
                    <LuX aria-hidden />
                  </button>
                </header>

                <div className="md-modal__body">
                  {quickViewLoading ? (
                    <p className="md-modal__muted">Loading ticket details...</p>
                  ) : null}
                  {quickViewError ? (
                    <p className="md-modal__muted" style={{ color: "#b42318" }}>
                      {quickViewError}
                    </p>
                  ) : null}

                  <section className="md-modal__section">
                    <h3>Ticket Details</h3>
                    <div className="md-modal__fields">
                      <div className="md-modal__field">
                        <span>Category</span>
                        <strong>{quickView.ticket.categoryLabel || "—"}</strong>
                      </div>
                      <div className="md-modal__field">
                        <span>Affected Module</span>
                        <strong>{quickView.ticket.affectedModule || "—"}</strong>
                      </div>
                      <div className="md-modal__field">
                        <span>Reference</span>
                        <strong>{quickView.ticket.referenceNumber || "—"}</strong>
                      </div>
                      <div className="md-modal__field">
                        <span>Assignee</span>
                        <strong>
                          {quickView.ticket.assignedToName || "Unassigned"}
                        </strong>
                      </div>
                      <div className="md-modal__field">
                        <span>Submitted</span>
                        <strong>{formatDateTime(quickView.ticket.createdAt)}</strong>
                      </div>
                      <div className="md-modal__field">
                        <span>Expected Response</span>
                        <strong>
                          {quickView.ticket.expectedResponseLabel || "—"}
                        </strong>
                      </div>
                    </div>
                  </section>

                  <section className="md-modal__section">
                    <h3>Description</h3>
                    <p className="md-modal__description">
                      {quickView.ticket.description || "No description provided."}
                    </p>
                  </section>

                  <section className="md-modal__section">
                    <h3>Attachments</h3>
                    {Array.isArray(quickView.attachments) &&
                    quickView.attachments.length > 0 ? (
                      <div style={{ display: "grid", gap: 8 }}>
                        {quickView.attachments.map((file) => (
                          <a
                            key={file.id}
                            className="md-attachment"
                            href={resolvePublicFileUrl(file.filePath)}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            <LuPaperclip aria-hidden />
                            {file.fileName || "Attachment"}
                          </a>
                        ))}
                      </div>
                    ) : (
                      <p className="md-modal__muted">
                        No attachment submitted with this ticket.
                      </p>
                    )}
                  </section>
                </div>

                <footer className="md-modal__footer">
                  <button
                    type="button"
                    className="md-btn md-btn--ghost"
                    onClick={closeQuickView}
                  >
                    Close
                  </button>
                  <button
                    type="button"
                    className="md-btn md-btn--primary"
                    onClick={() => {
                      closeQuickView();
                      openDetail(quickView.ticket);
                    }}
                  >
                    Open full details
                  </button>
                </footer>
              </div>
            </div>,
            document.body,
          )
        : null}
    </div>
  );
}
